import Operator from 'app/interfaces/Operator';
import User, {
  ChangePasswordRequest,
  CreateUserRequest,
  LoginUser,
  RolesCatalog,
  UpdateUserPermissionsRequest,
  UpdateUserPermissionsResponse,
  UpdateUserRequest,
} from 'app/interfaces/User';
import { buildIdentityHeaders } from 'app/lib/authFetch';
import { useSessionStore } from 'app/stores/globalStore';

export class UserConflictError extends Error {
  code: string;

  constructor(code: string, message: string) {
    super(message);
    this.name = 'UserConflictError';
    this.code = code;
  }
}

class UserService {
  private static instance: UserService;
  private baseUrl: string;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
  }

  public static getInstance(baseUrl?: string): UserService {
    if (!UserService.instance && baseUrl) {
      UserService.instance = new UserService(baseUrl);
    }
    return UserService.instance;
  }

  async getUsers(): Promise<User[]> {
    try {
      const response = await fetch(`${this.baseUrl}user/All`, {
        headers: this.buildHeaders(this.getToken()),
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      if (response.status === 204) return [];
      return response.json();
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  async getById(id: string): Promise<User | undefined> {
    try {
      const response = await fetch(`${this.baseUrl}user/${id}`, {
        headers: this.buildHeaders(this.getToken()),
      });
      if (response.status === 404) return undefined;
      if (!response.ok) throw new Error('Failed to fetch user');
      return response.json();
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  async create(request: CreateUserRequest, token?: string): Promise<User> {
    try {
      const response = await fetch(`${this.baseUrl}user`, {
        method: 'POST',
        headers: this.buildHeaders(this.getToken(token)),
        body: JSON.stringify(request),
      });
      await this.throwIfConflict(response);
      if (!response.ok) throw new Error('Failed to create user');
      return response.json();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  async update(
    id: string,
    request: UpdateUserRequest,
    token?: string
  ): Promise<User> {
    try {
      const response = await fetch(`${this.baseUrl}user/${id}`, {
        method: 'PUT',
        headers: this.buildHeaders(this.getToken(token)),
        body: JSON.stringify(request),
      });
      await this.throwIfConflict(response);
      if (!response.ok) throw new Error('Failed to update user');
      return response.json();
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  async changePassword(
    id: string,
    request: ChangePasswordRequest
  ): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}user/${id}/password`, {
        method: 'PUT',
        headers: this.buildHeaders(this.getToken()),
        body: JSON.stringify(request),
      });
      if (!response.ok) throw new Error('Failed to change password');
    } catch (error) {
      console.error('Error changing password:', error);
      throw error;
    }
  }

  async sendPasswordReset(id: string): Promise<void> {
    try {
      const response = await fetch(
        `${this.baseUrl}user/${id}/send-password-reset`,
        {
          method: 'POST',
          headers: this.buildHeaders(this.getToken()),
        }
      );
      await this.throwIfConflict(response);
      if (!response.ok) throw new Error('Failed to send password reset');
    } catch (error) {
      console.error('Error sending password reset:', error);
      throw error;
    }
  }

  async setStatus(id: string, active: boolean): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}user/${id}/status`, {
        method: 'PUT',
        headers: this.buildHeaders(this.getToken()),
        body: JSON.stringify({ active }),
      });
      if (!response.ok) throw new Error('Failed to update user status');
    } catch (error) {
      console.error('Error updating user status:', error);
      throw error;
    }
  }

  async getAvailableOperators(userId?: string): Promise<Operator[]> {
    try {
      const query = userId ? `?userId=${encodeURIComponent(userId)}` : '';
      const response = await fetch(
        `${this.baseUrl}user/availableoperators${query}`,
        { headers: this.buildHeaders(this.getToken()) }
      );
      if (!response.ok) throw new Error('Failed to fetch available operators');
      if (response.status === 204) return [];
      return response.json();
    } catch (error) {
      console.error('Error fetching available operators:', error);
      throw error;
    }
  }

  async getRoles(): Promise<RolesCatalog> {
    try {
      const response = await fetch(`${this.baseUrl}user/roles`, {
        headers: this.buildHeaders(this.getToken()),
      });
      if (!response.ok) throw new Error('Failed to fetch roles');
      return response.json();
    } catch (error) {
      console.error('Error fetching roles:', error);
      throw error;
    }
  }

  async updateUserPermissions(
    request: UpdateUserPermissionsRequest
  ): Promise<UpdateUserPermissionsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}user/permissions`, {
        method: 'PUT',
        headers: this.buildHeaders(this.getToken()),
        body: JSON.stringify(request),
      });
      if (!response.ok) throw new Error('Failed to update user permissions');
      return response.json();
    } catch (error) {
      console.error('Error updating user permissions:', error);
      throw error;
    }
  }

  async impersonate(id: string, token: string): Promise<LoginUser> {
    try {
      const response = await fetch(`${this.baseUrl}user/${id}/impersonate`, {
        method: 'POST',
        headers: buildIdentityHeaders(token),
      });
      await this.throwIfConflict(response);
      if (!response.ok) throw new Error('Failed to impersonate user');
      return response.json();
    } catch (error) {
      console.error('Error impersonating user:', error);
      throw error;
    }
  }

  async getMe(token: string): Promise<User | null> {
    try {
      const response = await fetch(`${this.baseUrl}user/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.status === 401) return null;
      if (!response.ok) throw new Error('Failed to fetch current user');
      return response.json();
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  }

  private getToken(explicitToken?: string): string | undefined {
    return explicitToken ?? useSessionStore.getState().loginUser?.token;
  }

  private buildHeaders(token?: string): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    return headers;
  }

  private async throwIfConflict(response: Response): Promise<void> {
    if (response.status !== 409) return;
    const body = (await response.json()) as { code?: string; message?: string };
    throw new UserConflictError(body.code ?? 'conflict', body.message ?? '');
  }
}

export const userService = UserService.getInstance(
  process.env.NEXT_PUBLIC_API_BASE_URL!
);

export default UserService;
